/**
 * SessionStorage utility for persisting deep link parameters across auth redirects
 *
 * When a user opens a deep link with query parameters (datasetId, linkType, query),
 * the auth flow redirects through /no-access and strips these parameters.
 * This utility saves and restores the parameters using sessionStorage.
 */

const STORAGE_KEY = "deepLinkParams";

export interface DeepLinkParams {
  // Portal params (used for routing, not restored to URL)
  datasetId?: string;
  path?: string; // Full relative path, e.g. "/researcher/wizards" or "/researcher/cohort"
  // All other query params (restored to URL for downstream apps to consume)
  queryParams?: Record<string, string>;
}

/**
 * Save deep link parameters to sessionStorage
 * @param params - Deep link parameters to save
 */
export const saveDeepLinkParams = (params: DeepLinkParams): void => {
  try {
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    }
  } catch (error) {
    console.warn("Failed to save deep link params to sessionStorage:", error);
  }
};

/**
 * Load deep link parameters from sessionStorage
 * @returns Saved parameters or null if none exist
 */
export const loadDeepLinkParams = (): DeepLinkParams | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.warn("Failed to load deep link params from sessionStorage:", error);
    return null;
  }
};

/**
 * Clear deep link parameters from sessionStorage (one-time use)
 */
export const clearDeepLinkParams = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear deep link params from sessionStorage:", error);
  }
};

/**
 * Extract deep link parameters from URL
 * @param url - Full URL or URLSearchParams object
 * @returns Deep link parameters found in URL
 */
export const extractDeepLinkParamsFromUrl = (url: string | URLSearchParams): DeepLinkParams => {
  // Whitelist of path suffixes that support deep linking with query params
  const DEEP_LINK_SUFFIXES = ["/cohort", "/wizards"];
  // Params consumed by portal (not restored to URL)
  const PORTAL_PARAMS = new Set(["datasetId"]);

  try {
    const searchParams = typeof url === "string" ? new URL(url).searchParams : url;
    const params: DeepLinkParams = {};

    // Check if pathname ends with a whitelisted suffix
    if (typeof url === "string") {
      const pathname = new URL(url).pathname;
      const matchedSuffix = DEEP_LINK_SUFFIXES.find((suffix) => pathname.endsWith(suffix));
      if (!matchedSuffix) {
        return {};
      }
      // Store the matched path for navigation (e.g., "/researcher/cohort")
      // Extract everything from /researcher onwards
      const researcherIndex = pathname.indexOf("/researcher");
      params.path = researcherIndex >= 0 ? pathname.slice(researcherIndex) : matchedSuffix;
    } else {
      // URLSearchParams passed directly - can't check path
      return {};
    }

    const datasetId = searchParams.get("datasetId");
    if (datasetId) params.datasetId = datasetId;

    // Collect all non-portal query params for downstream apps
    const queryParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (!PORTAL_PARAMS.has(key)) {
        queryParams[key] = value;
      }
    });
    if (Object.keys(queryParams).length > 0) {
      params.queryParams = queryParams;
    }

    return params;
  } catch (error) {
    console.warn("Failed to extract deep link params from URL:", error);
    return {};
  }
};
